import { useState, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X, Save, Loader2, Music } from 'lucide-react';
import FaqMediaFields from '@/components/FaqMediaFields';

/**
 * FAQ (con adjuntos) + audios POR PRODUCTO. Fuente de conocimiento de la IA.
 * Reusa FaqMediaFields (editor de media de FAQ de Configuración) y el mismo
 * esquema {question, answer, images, pdfs, videos}. Distinto almacén: db.products.faqs.
 * Guarda vía PUT /products/{id} (el backend normaliza con normalize_faq_items).
 */
export default function ProductFaqAudiosTab({ productId, initialFaqs = [], initialAudios = [] }) {
  const [faqs, setFaqs] = useState(initialFaqs);
  const [audios, setAudios] = useState(initialAudios);
  const [saving, setSaving] = useState(false);
  const [busyAudio, setBusyAudio] = useState(false);
  const audioRef = useRef(null);

  const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));

  const addFaq = () => setFaqs([...faqs, { id: newId(), question: '', answer: '', images: [], pdfs: [], videos: [] }]);
  const updateFaq = (i, next) => setFaqs(faqs.map((f, idx) => idx === i ? next : f));
  const removeFaq = (i) => setFaqs(faqs.filter((_, idx) => idx !== i));

  const uploadAudio = async (file) => {
    if (!file) return;
    setBusyAudio(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload/file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url || r.data?.path;
      if (url) setAudios([...audios, { url, title: file.name || 'Audio', kind: 'audio' }]);
    } catch (e) { toast.error('No se pudo subir el audio'); }
    finally { setBusyAudio(false); if (audioRef.current) audioRef.current.value = ''; }
  };
  const removeAudio = (i) => setAudios(audios.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${productId}`, { faqs, audios });
      toast.success('FAQ y audios guardados');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="product-faq-audios">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-white">FAQ del producto (con adjuntos)</Label>
        <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={addFaq} data-testid="pp-faq-add"><Plus className="w-3 h-3 mr-1" />Agregar FAQ</Button>
      </div>
      {faqs.length === 0 && <p className="text-[10px] text-muted-foreground">Sin FAQ. Agrega preguntas frecuentes de este producto para que la IA las use.</p>}
      {faqs.map((faq, i) => (
        <div key={faq.id || i} className="rounded-lg border border-white/10 bg-black/20 p-2.5 space-y-2" data-testid={`pp-faq-item-${i}`}>
          <div className="flex items-center gap-2">
            <Input value={faq.question || ''} onChange={e => updateFaq(i, { ...faq, question: e.target.value })} placeholder="Pregunta" className="bg-black/30 border-white/10 text-[11px]" />
            <button onClick={() => removeFaq(i)} className="text-muted-foreground hover:text-red-400 shrink-0" data-testid={`pp-faq-del-${i}`}><X className="w-3.5 h-3.5" /></button>
          </div>
          <Input value={faq.answer || ''} onChange={e => updateFaq(i, { ...faq, answer: e.target.value })} placeholder="Respuesta" className="bg-black/30 border-white/10 text-[11px]" />
          <FaqMediaFields faq={faq} onChange={next => updateFaq(i, next)} testId={`pp-faq-media-${i}`} />
        </div>
      ))}

      <div className="pt-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-white flex items-center gap-1"><Music className="w-3.5 h-3.5" />Audios del producto</Label>
          <div>
            <input ref={audioRef} type="file" accept="audio/*" className="hidden" onChange={e => uploadAudio(e.target.files?.[0])} />
            <Button size="sm" variant="outline" className="text-[10px] h-7" disabled={busyAudio} onClick={() => audioRef.current?.click()} data-testid="pp-audio-add">
              {busyAudio ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}Subir audio
            </Button>
          </div>
        </div>
        {audios.map((a, i) => (
          <div key={a.url || i} className="flex items-center gap-2 mt-1.5 text-[10px] text-emerald-200" data-testid={`pp-audio-${i}`}>
            <Music className="w-3 h-3 shrink-0" /><span className="truncate flex-1">{a.title || a.url}</span>
            <button onClick={() => removeAudio(i)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
          </div>
        ))}
      </div>

      <Button size="sm" className="w-full text-xs" disabled={saving} onClick={save} data-testid="pp-faq-save">
        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Guardar FAQ y audios
      </Button>
    </div>
  );
}
